import React, { FC, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { EditPencilIcon } from "assets/icons";
import { useCurrentUser, useAppDispatch } from "hooks";
import { updateMileageDeviationCommentOnlyThunk } from "app/features/mileageDeviations/mileageDeviationsAction";

interface MileageDeviationCommentCardProps {
  data: any;
}

const MileageDeviationCommentCard: FC<MileageDeviationCommentCardProps> = ({
  data
}) => {
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accountId, permissions, userEmail } = useCurrentUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editComment, setEditComment] = useState(data.comment || "");
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const hasEditPermission = permissions.canEditMileageDeviationComments;

  const handleSaveComment = async () => {
    if (!accountId) {
      return;
    }

    setIsLocalLoading(true);

    // Map status string to number
    const statusMap: { [key: string]: number } = {
      New: 0,
      Resolved: 1,
      Denied: 2
    };

    const currentStatusNumber = statusMap[data.status] ?? 0;

    try {
      try {
        const result = await dispatch(
          updateMileageDeviationCommentOnlyThunk({
            accountId,
            objectId: data.objectUniqueId,
            deviationId: data.uniqueId,
            comment: editComment,
            userEmail: userEmail || undefined
          })
        ).unwrap();

        setIsModalOpen(false);
      } catch (unwrapError) {
        setTimeout(() => {
          setIsModalOpen(false);
        }, 500);

        throw unwrapError;
      }
    } catch (error) {
      console.error("Error on update mileage deviation comment");
    } finally {
      setIsLocalLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditComment(data.comment || "");
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setEditComment(data.comment || "");
    setIsModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleDateString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F9FBFF",
        padding: 0,
        margin: 0,
        borderRadius: 0,
        border: "none"
      }}
    >
      {/* Layout */}
      <Box
        sx={{
          padding: 2,
          backgroundColor: "#FFF",
          borderBottom: "1px solid #E0E0E0"
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 2fr 2fr 1fr",
            alignItems: "center",
            mb: 0,
            backgroundColor: "#F9FBFF",
            padding: "12px 16px",
            marginX: -2,
            marginTop: -2,
            borderBottom: "1px solid #E0E0E0"
          }}
        >
          {/* Comment sub-headers */}
          <Typography
            variant="body2"
            fontWeight={600}
            color="#959eb3"
            sx={{ textTransform: "uppercase", fontSize: "12px", marginLeft: 5 }}
          >
            {t("tableHeading.commenter", { ns: "tableHeadingsTranslation" })}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color="#959eb3"
            sx={{ textTransform: "uppercase", fontSize: "12px" }}
          >
            {t("tableHeading.date", { ns: "tableHeadingsTranslation" })}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color="#959eb3"
            sx={{ textTransform: "uppercase", fontSize: "12px" }}
          >
            {t("tableHeading.note", { ns: "tableHeadingsTranslation" })}
          </Typography>
        </Box>

        {/* Data row with column-like layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 2fr 2fr 1fr",
            alignItems: "flex-start",
            paddingY: "16px",
            gap: 1
          }}
        >
          {/* Commenter - first column */}
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              sx={{ marginLeft: 5, fontSize: "14px" }}
            >
              {data.commenterUserName || "-"}
            </Typography>
          </Box>

          {/* Date - second column */}
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              sx={{ fontSize: "14px" }}
            >
              {data.commentedOn ? formatDate(data.commentedOn) : "-"}
            </Typography>
          </Box>

          {/* Note - third column */}
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              sx={{ fontSize: "14px" }}
            >
              {data.comment || t("common.noCommentClickToAdd")}
            </Typography>
          </Box>

          {/* Action Button - fourth column */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {hasEditPermission && (
              <IconButton
                onClick={handleOpenModal}
                size="small"
                sx={{
                  color: "#00B2A3",
                  "&:hover": {
                    backgroundColor: "rgba(0, 178, 163, 0.08)"
                  }
                }}
              >
                <EditPencilIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Edit Comment Modal */}
        <Dialog
          open={isModalOpen}
          onClose={handleCancelEdit}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              gap: 2
            }
          }}
        >
          <DialogTitle sx={{ fontSize: "20px", fontWeight: 600, pb: 1 }}>
            {t("titles.editCommentFor", {
              objectName: data.objectName || "Object"
            })}
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              color={theme.palette.text.secondary}
              sx={{ mb: 2, fontWeight: 500 }}
            >
              {t("common.comment")}
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={editComment}
              onChange={(e) => setEditComment(e.target.value)}
              placeholder={t("common.enterNote")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF"
                }
              }}
            />
          </DialogContent>
          <Box
            sx={{
              borderTop: "1px solid #E0E0E0",
              mx: 3,
              mb: 1
            }}
          />
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
            <Button
              variant="text"
              text={t("buttonsText.cancel")}
              onClick={handleCancelEdit}
              sx={{
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "transparent"
                }
              }}
            />
            <Button
              variant="contained"
              text={t("buttonsText.saveChanges")}
              onClick={handleSaveComment}
              loading={isLocalLoading}
              disabled={isLocalLoading}
              sx={{
                backgroundColor: "#00B2A3",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#009688"
                }
              }}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MileageDeviationCommentCard;
